import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { CTASection } from "@/components/CTASection";
import { InlineCTA } from "@/components/InlineCTA";
import { YouTubeShortsSection } from "@/components/YouTubeShortsSection";
import { MapPin } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import heroImage from "@/assets/stone-steps-masonry-franklin-lakes-nj.webp";
import retainingWallImage from "@/assets/masonry-pro-stone-retaining-wall-franklin-lakes-nj.webp";
import drivewayImage from "@/assets/bergen-county-driveway-paver-installation-leonia-nj.webp";
import permitsImage from "@/assets/retaining-wall-hackensack-bergen-county-nj.webp";

const faqs = [
  {
    question: "Do I need a permit for a paver patio in Bergen County?",
    answer:
      "Most Bergen County municipalities do not require a permit for a standard at-grade patio. However, if your project includes retaining walls, electrical, or gas lines, a UCC review may be required. Always confirm with your borough's building department before starting work.",
  },
  {
    question: "What is the best time of year to start a masonry project in Bergen County?",
    answer:
      "Spring and early fall offer the best conditions for concrete curing and base compaction. April through May and September through October are the most productive months. Summer work is possible with schedule adjustments. Winter pours require extra precautions and typically cost more.",
  },
  {
    question: "How do Bergen County's clay soils affect paver and retaining wall installations?",
    answer:
      "Clay-rich glacial soils like the Boonton and Rockaway series retain water and heave during freeze-thaw cycles. Without a properly compacted gravel base and geotextile fabric, pavers will settle and walls will shift. Base preparation is the most important step in any hardscape project on Bergen County soil.",
  },
  {
    question: "How long does an outdoor kitchen last in New Jersey weather?",
    answer:
      "A masonry outdoor kitchen with annual sealing and proper winterization can last 20 to 30 years. Key maintenance includes sealing stone countertops before the first frost, protecting plumbing lines from freezing, and covering appliances with breathable, vented covers rather than plastic tarps.",
  },
  {
    question: "Which Bergen County towns have the strictest masonry permit requirements?",
    answer:
      "Requirements vary across all 70 municipalities. Larger towns like Hackensack, Teaneck, and Fort Lee tend to have more detailed review processes. Any project involving walls over 4 feet or gas and electric hookups will generally require a permit regardless of which borough you're in.",
  },
  {
    question: "Will heavy equipment damage my lawn or driveway during installation?",
    answer:
      "Tight lot lines in boroughs like Bergenfield, Dumont, and Teaneck require careful staging plans. We use plywood sheeting to protect lawns and run smaller equipment like mini excavators and walk-behind compactors on most Bergen County residential lots. Access and staging are part of every project plan we put together.",
  },
];

const municipalities = [
  "Alpine", "Bergenfield", "Bogota", "Carlstadt", "Cliffside Park", "Closter",
  "Dumont", "East Rutherford", "Edgewater", "Elmwood Park", "Englewood",
  "Fair Lawn", "Fort Lee", "Franklin Lakes", "Garfield", "Glen Rock",
  "Hackensack", "Hasbrouck Heights", "Haworth", "Hillsdale", "Ho-Ho-Kus",
  "Leonia", "Lodi", "Lyndhurst", "Mahwah", "Maywood", "Midland Park",
  "New Milford", "North Arlington", "Northvale", "Norwood", "Oakland",
  "Old Tappan", "Oradell", "Palisades Park", "Paramus", "Park Ridge",
  "Ramsey", "Ridgefield", "Ridgewood", "River Edge", "River Vale",
  "Rochelle Park", "Rutherford", "Saddle Brook", "Saddle River",
  "South Hackensack", "Teaneck", "Tenafly", "Upper Saddle River",
  "Waldwick", "Wallington", "Washington Township", "Westwood",
  "Wood-Ridge", "Woodcliff Lake", "Wyckoff",
];

export default function BergenCounty() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Masonry Contractor Bergen County NJ | Masonry Pro</title>
        <meta name="description" content="Planning a patio, retaining wall, or outdoor kitchen in Bergen County, NJ? We know the soils, the permits, and the builds that hold up here for decades." />
        <meta property="og:description" content="Planning a patio, retaining wall, or outdoor kitchen in Bergen County, NJ? We know the soils, the permits, and the builds that hold up here for decades." />
        <meta property="og:title" content="Masonry Contractor Bergen County NJ | Masonry Pro" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
            }))
          })}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/bergen-county.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/bergen-county.jpg" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />

        <main>
          {/* Hero Section */}
          <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '80px' }}>
            <img src={heroImage} alt="Masonry contractor serving Bergen County, NJ" width={1200} height={800} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,16,23,0.45) 0%, rgba(13,16,23,0.65) 100%)' }} />
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-4xl mx-auto text-center">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <MapPin className="h-6 w-6" style={{ color: '#d4a853' }} />
                  <span style={{ color: '#d4a853', fontWeight: 500 }}>Bergen County, NJ</span>
                </div>
                <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1.5rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                  Custom Hardscaping and Masonry for{" "}
                  <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>Bergen County</span> Homes, from Ridgewood to Fort Lee
                </h1>
                <p style={{ color: 'rgba(225,231,239,0.85)', fontSize: '1.25rem', maxWidth: '42rem', margin: '0 auto', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                  Serving all 70 municipalities with expert hardscape and masonry work built for North Jersey conditions.
                </p>
              </div>
            </div>
          </section>

          {/* Intro Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto space-y-6 text-lg text-muted-foreground">
                <p>
                  <a href="https://bergencountynj.gov/" target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">Bergen County</a><sup className="text-secondary">1</sup>{" "}
                  stretches from the Palisades Cliffs to the Ramapo foothills. It comprises 70 municipalities and has some of the most diverse housing stock in North Jersey. You'll find prewar Tudors in Ridgewood, sitting a few towns over from split-levels in Paramus.
                </p>
                <p>
                  This page covers the masonry installations Bergen County homeowners ask us about most.{" "}
                  <Link to="/paving-services/paver-patios" className="text-secondary hover:underline">Paver patios</Link>.{" "}
                  <Link to="/paving-services/paver-walkways" className="text-secondary hover:underline">Walkways</Link> and{" "}
                  <Link to="/paving-services/paver-driveways" className="text-secondary hover:underline">driveways</Link>.{" "}
                  <Link to="/masonry-services/retaining-walls" className="text-secondary hover:underline">Retaining walls</Link> on sloped lots.{" "}
                  <Link to="/masonry-services/fire-pits" className="text-secondary hover:underline">Outdoor fireplaces</Link> and full{" "}
                  <Link to="/masonry-services/outdoor-kitchens" className="text-secondary hover:underline">kitchen builds</Link>.{" "}
                  <Link to="/waterproofing-services/basement-waterproofing" className="text-secondary hover:underline">Basement waterproofing</Link> and{" "}
                  <Link to="/waterproofing-services/patio-waterproofing" className="text-secondary hover:underline">patio waterproofing</Link>. Each one has its own set of local factors that affect how the job is done right.
                </p>
                <p>
                  Bergen County's 70 boroughs and townships each have their own building department. Permit forms, fees, and turnaround times change from one town to the next. The soil here adds another layer. Boonton and Rockaway series clays, formed from glacial till, retain more water and become harder through freeze-thaw cycles. A crew that doesn't account for that is setting up a callback.
                </p>
                <p>
                  Tight lot lines in towns like Bergenfield, Teaneck, and Dumont also affect how we stage equipment and coordinate around your neighbors. These are things you learn from working in the county, not from reading about it.
                </p>
                <p>
                  We're available for on-site walkthroughs across Bergen County. Eastern Palisades communities to the western Ramapo foothills. Send us a few photos or give us a call or text and we can go from there.
                </p>
              </div>
            </div>
          </section>

          {/* CTA 1 */}
          <InlineCTA heading="Thinking About a Project?" highlightedHeading="Check Availability" description="Call or text us directly or scroll down to request your free estimate." />

          {/* Paver Patios Section - image left, text right */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="aspect-[4/3] rounded-xl overflow-hidden border border-border">
                  <img src={retainingWallImage} alt="Masonry Pro construction project of stone work retaining wall with steps in Franklin Lakes, Bergen County, New Jersey." className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="space-y-6">
                  <h2 className="text-3xl md:text-4xl font-bold">
                    Paver Patios and Walkways That Survive{" "}
                    <span className="text-gradient-gold">35+ Freeze-Thaw Cycles</span> Each Winter
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      Thinking about replacing that cracked concrete patio? If you own a Colonial in Glen Rock or a Tudor in Ridgewood, you already know what North Jersey winters can do to a slab. One season of freeze-thaw is enough to open up cracks. A few more, and the surface begins to break apart.
                    </p>
                    <p>
                      Interlocking pavers handle this differently. Each unit moves independently through freeze-thaw cycles instead of cracking as a single piece. If one paver is damaged, remove it and replace it. No tearing out the whole surface.
                    </p>
                    <p>
                      Bergen County sees roughly 48 inches of rain per year<sup className="text-secondary">2</sup> and anywhere from 35 to 45 freeze-thaw cycles each winter. That combination is hard on any outdoor surface. The difference between a patio that holds up and one that doesn't usually comes down to what's underneath it.
                    </p>
                    <p>
                      For Bergen County's clay-heavy soils, we install a compacted gravel base several inches thick. That base prevents water from pooling under the pavers and allows the surface to flex without shifting. We finish the joints with polymeric sand, which locks units together and keeps weeds from pushing through.
                    </p>
                    <p>
                      If you're still in the budgeting phase, here's a breakdown of <Link to="/pro-tips/paver-patio-cost-north-jersey" className="text-secondary hover:underline">what a paver patio costs in North Jersey</Link> and the factors that move the number.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Retaining Walls Section - text left, image right */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="space-y-6">
                  <h2 className="text-3xl md:text-4xl font-bold">
                    Retaining Walls Engineered for{" "}
                    <span className="text-gradient-gold">Bergen County's Glacial Clay Soils</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      Sloped yards are common in western Bergen County. Mahwah, Ramsey, Saddle River, Franklin Lakes. Many of these properties sit on land that drops several feet between the house and the property line. Without proper wall construction, that soil moves.
                    </p>
                    <p>
                      Bergen County's Boonton series soils formed from glacial till containing shale, basalt, and diabase<sup className="text-secondary">3</sup>. They're acidic and clay-heavy. When it rains, they hold water. When that water freezes, the soil expands and pushes against anything in its path. A retaining wall that doesn't account for this kind of lateral pressure will lean, crack, or fail within a few years.
                    </p>
                    <p>
                      Walls over 4 feet typically require engineered drawings and municipal review in most Bergen County boroughs. That's not red tape for the sake of it. At that height, the soil pressure behind the wall can be significant, and the footing, drainage, and reinforcement all need to be sized correctly.
                    </p>
                    <p>
                      Properties near the Ramapo Valley County Reservation in Mahwah and the Campgaw Mountain area often have steeper grades and heavier clay content. We've worked with homeowners in Saddle River's larger estate lots where a single wall project involved managing grade changes across 50 or more linear feet.
                    </p>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden border border-border">
                  <img src={drivewayImage} alt="Newly installed paver driveway in Leonia, Bergen County, NJ by Masonry Pro Construction, expert masonry and pavers" className="w-full h-full object-cover" loading="lazy" />
                </div>
              </div>
            </div>
          </section>

          {/* Outdoor Kitchens Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
                  Outdoor Kitchens and Fireplaces Built to Handle{" "}
                  <span className="text-gradient-gold">North Jersey's Four-Season Weather</span>
                </h2>
                <div className="space-y-6 text-lg text-muted-foreground">
                  <p>
                    Outdoor kitchen projects have been in high demand in the past few years. Homeowners in Paramus, Fair Lawn, and Teaneck are investing in backyard space they can actually use from April through November. Families near Overpeck County Park and Van Saun County Park tend to spend a lot of time outside, and a well-built{" "}
                    <Link to="/masonry-services/outdoor-kitchens" className="text-secondary hover:underline">outdoor kitchen</Link> makes that easier.
                  </p>
                  <p>
                    Masonry-built outdoor kitchens and{" "}
                    <Link to="/masonry-services/fire-pits" className="text-secondary hover:underline">fireplaces</Link>{" "}
                    use stone, brick, and concrete block. These materials handle summer heat above 95 degrees and winter lows well below freezing. Prefabricated metal alternatives don't perform as well in this climate.
                  </p>
                  <p>
                    Here's the thing most people don't think about until it's too late: winterization. All plumbing lines, grout joints, and stone countertops need annual sealing before the first frost. Without it, moisture gets into small cracks and expands when it freezes. That causes spalling on stone surfaces and cracked grout lines by spring.
                  </p>
                  <p>
                    Spring and early fall are the best windows for concrete curing on these projects. Mid-project winter pauses add cost and timeline delays. If you're thinking about a kitchen or fireplace build, planning around the weather saves you money.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Driveways Section - image left, text right */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="aspect-[3/4] rounded-xl overflow-hidden border border-border">
                  <img src={permitsImage} alt="New retaining wall in Hackensack, Bergen County, New Jersey, 07601 by Masonry Pro Construction." className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="space-y-6">
                  <h2 className="text-3xl md:text-4xl font-bold">
                    Driveway Installations Matched to{" "}
                    <span className="text-gradient-gold">Bergen County Home Styles</span> and Lot Sizes
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      Many Bergen County driveways are asphalt that has passed its life expectancy. Cape Cods in Bergenfield, Colonials in River Edge, and split-levels in Dumont. The asphalt is installed, lasts 15 to 20 years, and then begins to crumble at the edges. Road salt can speed that up. A <Link to="/concrete-services/driveways" className="text-secondary hover:underline">concrete driveway</Link> is a durable alternative that stands up to North Jersey winters when poured correctly.
                    </p>
                    <p>
                      <Link to="/paving-services/paver-driveways" className="text-secondary hover:underline">Paver driveways</Link> outlast asphalt in this climate. They resist salt damage, and they're repairable. If a tree root pushes up a section, you pull those pavers, fix the base, and reset them. With asphalt, a root heave means patching or repaving.
                    </p>
                    <p>
                      Many Bergen County boroughs have narrow lot frontages. In Bergenfield, Dumont, and Hackensack, 50 to 75 feet of frontage is common. That means careful layout planning so the driveway works with the house, the sidewalk, and whatever space is left for landscaping.
                    </p>
                    <p>
                      Some towns require a soil erosion certification from the Bergen County Soil Conservation District in Oradell<sup className="text-secondary">4</sup> for projects that disturb a significant amount of ground. We handle permit coordination as part of the project, so you don't have to chase paperwork between offices.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA 2 */}
          <InlineCTA heading="Questions About Permits?" highlightedHeading="We Can Help" description="Give us a call or text, or fill out the form below, and we handle permits across all 70 Bergen County towns." />

          {/* Permits Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
                  What Bergen County Permit Rules Mean for{" "}
                  <span className="text-gradient-gold">Your Masonry Project</span>
                </h2>
                <div className="space-y-6 text-lg text-muted-foreground">
                  <p>
                    Bergen County has 70 municipalities<sup className="text-secondary">5</sup>. Each one runs its own building department with its own forms, fees, and review timeline. There is no single county-wide permit office for residential construction. Every project goes through the borough or township where the property sits.
                  </p>
                  <p>
                    All 70 municipalities follow the{" "}
                    <a href="https://www.nj.gov/dca/codes/codreg/pdf_regs/njac_5_23_2.pdf" target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">New Jersey Uniform Construction Code</a><sup className="text-secondary">6</sup>. The code itself is consistent across the state, but how quickly your application gets reviewed and what supporting documents are required can vary from town to town.
                  </p>
                  <p>
                    Not every masonry project needs a permit. A standard at-grade paver patio usually does not. But{" "}
                    <Link to="/masonry-services/retaining-walls" className="text-secondary hover:underline">retaining walls</Link> over 4 feet,{" "}
                    <Link to="/masonry-services/outdoor-kitchens" className="text-secondary hover:underline">outdoor kitchens</Link> with gas or electric hookups, and any structural masonry work will trigger a UCC review. Hackensack, Teaneck, and Fair Lawn each have distinct requirements and turnaround times.
                  </p>
                  <p>
                    Projects involving soil disturbance may also require Bergen County Soil Conservation District certification. We know the process in each municipality we work in, and we can tell you upfront whether your project needs a permit and what the timeline looks like.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Service Area Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="text-3xl md:text-4xl font-bold mb-8">
                  Serving Properties Across{" "}
                  <span className="text-gradient-gold">Bergen County</span>, from Hackensack to Mahwah
                </h2>
                <div className="space-y-6 text-lg text-muted-foreground text-left">
                  <p>
                    Bergen County covers a lot of ground. The eastern edge runs along the Hudson River waterfront: Fort Lee, Edgewater, Palisades Park. The suburban center includes Paramus, Hackensack, and Teaneck. Head west and you reach the wooded hills near Mahwah, Ramsey, and Saddle River, close to the 1,373-acre Campgaw Mountain Reservation<sup className="text-secondary">7</sup>.
                  </p>
                  <p>
                    That geographic spread means the soil conditions, lot sizes, and home styles change as you move across the county. A paver patio in Paramus near Van Saun County Park sits on different ground than a retaining wall project in Saddle River. The permitting process is different too. Each municipality has its own building department, its own forms, and its own review speed.
                  </p>
                  <p>
                    We've worked on properties along the Kinderkamack Road corridor, near Ridgewood's prewar downtown, and on estate lots in the Saddle River and Upper Saddle River area. That kind of county-wide experience means we already know what to expect when we pull up to your property.
                  </p>
                </div>
                <div className="mt-8">
                  <h3 className="text-xl font-bold mb-4">Bergen County Municipalities We Serve</h3>
                  <div className="flex flex-wrap justify-center gap-3">
                    {municipalities.map((town) => (
                      <span
                        key={town}
                        className="bg-background rounded-full px-4 py-2 border border-border text-sm text-muted-foreground"
                      >
                        {town}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA 3 */}
          <CTASection serviceName="Bergen County masonry" heading="Ready to Start?" highlightedHeading="Schedule Your Estimate" />

          {/* YouTube Shorts Section */}
          <YouTubeShortsSection
            videoIds={["hMO7o0DWqAw", "qBJO5fKXbPI", "cFHz4fwoL34"]}
            title="See Our Bergen County"
            highlightedTitle="Work in Action"
          />

          {/* FAQ Section - Card Grid */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
                Frequently Asked <span className="text-gradient-gold">Questions</span>
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="bg-background rounded-xl p-6 border border-border"
                  >
                    <h3 className="text-lg font-bold mb-3">{faq.question}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Citations Accordion */}
          <section className="py-12">
            <div className="container mx-auto px-4 max-w-4xl">
              <Accordion type="single" collapsible>
                <AccordionItem value="citations" className="border-border">
                  <AccordionTrigger className="text-sm text-muted-foreground hover:no-underline">
                    Source Citations
                  </AccordionTrigger>
                  <AccordionContent>
                    <ol className="list-decimal list-inside space-y-3 text-sm text-muted-foreground">
                      <li>
                        Bergen County, New Jersey: Official Bergen County Government website.
                      </li>
                      <li>
                        Bergen County Climate Data (approximately 48 inches of annual rainfall): BestPlaces.net.
                      </li>
                      <li>
                        Official Series Description, Boonton Series (glacial till containing shale, basalt, and diabase): USDA NRCS.
                      </li>
                      <li>
                        Bergen County Soil Conservation District, Oradell, NJ.
                      </li>
                      <li>
                        Bergen County, NJ Municipalities (70 boroughs and townships).
                      </li>
                      <li>
                        New Jersey Uniform Construction Code (N.J.A.C. 5:23): NJ Dept. of Community Affairs.
                      </li>
                      <li>
                        Campgaw Mountain Reservation (1,373 acres): Bergen County Parks.
                      </li>
                    </ol>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
